import React, { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import debounce from "lodash.debounce";
import { Player } from "@lottiefiles/react-lottie-player";
import animationData from "../../assets/images/bearExplanation/scroll.json";
import "./myBear.sass";
import DOMPurify from "dompurify";
import { useDispatch, useSelector } from "react-redux";
import { updateStatus } from "../../actions/userActions";
import image from "./image";
import equipmentImage from "../../assets/js/equipmentImage";
import canvasEquipmentImage from "../../assets/js/canvasEquipmentImage";
import usePage from "../../hooks/usePage";
import Progress from "./components/Progress";

export default function MyBear() {
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();
  const [showEquipment, setShowEquipment] = useState(false);
  const statusData = useSelector((state) => state.user.statusData);
  const bearSetInfo = useSelector((state) => state.user.bearSetInfo);
  const userName = useSelector((state) => state.user.userName);
  const canvasRef = useRef(null);
  const hiddenCanvasRef = useRef(null);
  const [imagesToDraw, setImagesToDraw] = useState([]);
  const [backgroundInfo, setBackgroundInfo] = useState({});
  const [completePercent, setCompletePercent] = useState(0);

  // 頁面標題更新
  usePage({ title: t(`myBear.title`) });

  // 獲取用戶狀態資訊
  useEffect(() => {
    // dispatch(123));
  }, [dispatch]);

  // 從 API 整合和處理裝備數據
  const { user_no, user_lang, bear_type, user_progress, bear_set, login_at } =
    statusData ? statusData : {};

  const mergedData = (bear_set || [])
    .filter((apiData) => apiData.categoryId !== 7) // 排除背景
    .map((apiData) => {
      const category = bearSetInfo.find(
        (category) => category.categoryId === apiData.categoryId,
      );
      const equipment = []; // 初始化空数组以便在不同条件下添加装备

      if (!apiData.isLock) {
        // 只有当 isLock 为 false 时，才处理并映射 apiData.equipment
        apiData.equipment.forEach((apiEquip) => {
          const equip = category?.equipment.find((e) => e.id === apiEquip.id);
          if (equip) {
            equipment.push({
              ...equip,
              isWear: apiEquip.isWear,
              isNew: apiEquip.isNew,
            });
          }
          // else {
          //   equipment.push(apiEquip);
          // }
        });
      } else {
        // 如果 isLock 为 true，添加锁定的默认装备
        equipment.push(
          ...Array(3).fill({
            id: 0,
            isNew: false,
            name: { zh: "-", en: "-", jp: "-" },
            imageInfo: [{ listSrc: image.equipmentLock }],
          }),
        );
      }
      return { ...category, equipment, isLock: apiData.isLock };
    });

  const backgroundData = (bear_set || [])
    .filter((apiData) => apiData.categoryId === 7)
    .map((apiData) => {
      const category = bearSetInfo.find(
        (category) => category.categoryId === apiData.categoryId,
      );
      const equipment = []; // 初始化空数组以便在不同条件下添加装备

      apiData.equipment.forEach((apiEquip) => {
        const equip = category?.equipment.find((e) => e.id === apiEquip.id);
        if (equip) {
          equipment.push({
            ...equip,
            isWear: apiEquip.isWear,
            isNew: apiEquip.isNew,
          });
        }
        // else {
        //   equipment.push(apiEquip);
        // }
      });
      for (let i = equipment.length; i < 3; i++) {
        equipment.push({
          id: 0,
          isNew: false,
          name: { zh: "-", en: "-", jp: "-" },
          imageInfo: [{ canvasBg: image.equipmentLock }],
        });
      }
      return { ...category, equipment, isLock: apiData.isLock };
    });

  const loadImage = (src) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });

  const [wearList, setWearList] = useState([]);

  const defaultImageInfo = {
    src: canvasEquipmentImage.face00,
    listSrc: equipmentImage.face00,
    categoryId: 3,
    equipmentId: null, // 可以设为 null 或适当的标识
    width: 142,
    height: 142,
    x: 905,
    y: 491,
    zIndex: 4,
  };

  const wearEquipmentHandler = (
    isSelect = false,
    imageInfos,
    categoryId,
    equipmentId,
  ) => {
    // 更新图片信息

    setImagesToDraw((prev) => {
      let listUpdated = [...prev];

      // 移除当前categoryId的旧图像
      listUpdated = listUpdated.filter((img) => img.categoryId !== categoryId);

      // 如果是点击选择，并且选中的装备已存在
      const exists = prev.some(
        (img) =>
          img.categoryId === categoryId &&
          imageInfos.some((info) => info.src === img.src),
      );

      if (exists && isSelect) {
        // 如果选中的装备已存在，并且是由点击事件触发的，则移除
        listUpdated = listUpdated.filter(
          (img) => !imageInfos.some((info) => info.src === img.src),
        );
      } else {
        // 否则添加新图像
        const newImages = imageInfos.map((info) => ({
          ...info,
          categoryId,
          imgPromise: loadImage(info.src),
        }));
        listUpdated = [...listUpdated, ...newImages];
      }

      // 检查是否存在categoryId=3的项目
      const hasCategoryThree = listUpdated.some((img) => img.categoryId === 3);
      if (!hasCategoryThree) {
        // 如果不存在，则添加默认图片
        listUpdated.push({
          ...defaultImageInfo,
          imgPromise: loadImage(defaultImageInfo.src),
        });
      }

      return listUpdated;
    });

    if (!categoryId) return;

    // 更新穿戴列表
    setWearList((prevList) => {
      const existingIndex = prevList.findIndex(
        (item) =>
          item.categoryId === categoryId && item.equipmentId === equipmentId,
      );
      if (existingIndex >= 0 && isSelect) {
        // 如果已选中，并且是由点击事件触发的，则从列表中移除
        return prevList.filter((_, index) => index !== existingIndex);
      } else {
        // 如果未选中，移除同一类别的旧装备并添加新选择的装备
        const filteredList = prevList.filter(
          (item) => item.categoryId !== categoryId,
        );
        return [...filteredList, { categoryId, equipmentId }];
      }
    });
  };

  const draw = useCallback(() => {
    console.log("draw");
    const displayCanvas = canvasRef.current;
    const hiddenCanvas = hiddenCanvasRef.current;
    if (displayCanvas && hiddenCanvas) {
      const displayCtx = displayCanvas.getContext("2d");
      const hiddenCtx = hiddenCanvas.getContext("2d");

      displayCtx.clearRect(0, 0, displayCanvas.width, displayCanvas.height);
      hiddenCtx.clearRect(0, 0, hiddenCanvas.width, hiddenCanvas.height);

      const promises = imagesToDraw.map((image) =>
        image.imgPromise.then((img) => ({
          img,
          ...image,
        })),
      );

      canvasRef.current.width = 1920;
      Promise.all(promises)
        .then((images) => {
          images
            .sort((a, b) => a.zIndex - b.zIndex)
            .forEach(({ img, x, y, width, height }) => {
              displayCtx.drawImage(
                img,
                x * (canvasRef.current.width / 1920),
                y * (canvasRef.current.height / 1080),
                width * (canvasRef.current.width / 1920),
                height * (canvasRef.current.height / 1080),
              );
              hiddenCtx.drawImage(img, x, y, width, height);
            });
        })
        .catch((error) => console.error("Failed to load images", error));
    }
  }, [imagesToDraw]); // 將 imagesToDraw 添加到依賴列表，確保當 imagesToDraw 變化時重新創建 draw 函數

  useEffect(() => {
    draw();
  }, [imagesToDraw]); // 当 imagesToDraw 更新时重绘

  // 用於切換裝備顯示的處理函數
  const showEquipmentHandler = (isShow) => setShowEquipment(isShow);

  const initBearCanvas = () => {
    mergedData.forEach((category) => {
      category.equipment.forEach((equip) => {
        if (equip.isWear) {
          wearEquipmentHandler(
            false,
            equip.imageInfo,
            category.categoryId,
            equip.id,
          );
        }
      });
    });
    backgroundData[0].equipment.forEach((background) => {
      if (background.isWear) {
        setBackgroundInfo({
          id: background.id,
          imageInfo: background.imageInfo[0],
        });
        // changeBackgroundHandler(background.id, background.imageInfo[0]);
      }
    });
  };

  // 初始化裝備
  useEffect(() => {
    console.log(`output->statusData`, statusData);
    if (statusData === null) return;
    // 尋找statusData.user_progress底下progress非0的項目，然後計算完成百分比
    const completeCount = statusData.user_progress.filter(
      (item) => item.progress !== 0,
    ).length;
    setCompletePercent(
      Math.round((completeCount / statusData.user_progress.length) * 100),
    );
    console.log(completePercent);

    // 根據bear_type選擇不同的熊熊圖片
    bear_type === 1 &&
      wearEquipmentHandler(false, [
        {
          src: image.bearSrc01,
          width: 986,
          height: 571,
          x: 477,
          y: 400,
          zIndex: 2,
        },
      ]);
    bear_type === 2 &&
      wearEquipmentHandler(false, [
        {
          src: image.bearSrc02,
          width: 986,
          height: 571,
          x: 477,
          y: 400,
          zIndex: 2,
        },
      ]);
    bear_type === 3 &&
      wearEquipmentHandler(false, [
        {
          src: image.bearSrc03,
          width: 986,
          height: 571,
          x: 477,
          y: 400,
          zIndex: 2,
        },
      ]);
    bear_type === 4 &&
      wearEquipmentHandler(false, [
        {
          src: image.bearSrc04,
          width: 986,
          height: 571,
          x: 477,
          y: 400,
          zIndex: 2,
        },
      ]);
    // 判斷有哪些裝備isWear為true，然後將其觸發wearEquipmentHandler
    if (mergedData) {
      initBearCanvas();
    }
  }, [statusData]);

  const handleSave = (isCenter) => {
    const width = isCenter ? 600 : 1920;
    const height = isCenter ? 600 : 1080;

    const canvas = hiddenCanvasRef.current; // 这个 Canvas 可能包含了你想保存的内容
    // const ctx = canvas.getContext("2d");

    // 创建一个新的临时 canvas 来调整大小和绘制
    const tempCanvas = document.createElement("canvas");
    const tCtx = tempCanvas.getContext("2d");
    tempCanvas.width = 1920; // 设为大一点以适应背景图的全尺寸
    tempCanvas.height = 1080;

    // 定义加载图像的函数
    const loadImage = (src) =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
      });

    // 生成背景图和Logo图的URL
    const backgroundUrl =
      Object.keys(backgroundInfo).length === 0
        ? image.canvasBgSmall00
        : isCenter
          ? backgroundInfo.imageInfo.canvasBgSmall
          : backgroundInfo.imageInfo.canvasBg;

    // 同时加载背景图和Logo图
    Promise.all([
      loadImage(`${backgroundUrl}?timestamp=${new Date().getTime()}`),
      loadImage(image.canvasLogo),
      loadImage(image.canvasName),
    ])
      .then(([backgroundImage, logoImage, nameImage]) => {
        // 绘制背景图
        tCtx.drawImage(
          backgroundImage,
          isCenter ? 460 : 0,
          isCenter ? 40 : 0,
          isCenter ? 1000 : width,
          isCenter ? 1000 : height,
        );

        // 将另一个 canvas 的内容绘制到这个 canvas 上
        tCtx.drawImage(
          canvas,
          0,
          0,
          canvas.width,
          canvas.height,
          0,
          0,
          1920,
          1080,
        );

        tCtx.font = "28px Arial"; // 这里可以根据你的需求调整字体样式
        tCtx.fillStyle = "white";
        tCtx.textAlign = "center"; // 水平居中
        tCtx.textBaseline = "middle"; // 垂直居中

        if (isCenter) {
          tCtx.drawImage(logoImage, 506, 88, 163, 146);
          tCtx.drawImage(nameImage, 755.5, 122, 383, 81);
          // 寫入文字
          const name = userName;
          tCtx.fillText(name, 844 + 206 / 2, 122 + 87 / 2);
        } else {
          tCtx.drawImage(logoImage, 70, 52, 175, 157);
          tCtx.drawImage(nameImage, 781.5, 88, 378, 87);
          // 寫入文字
          const name = userName;
          tCtx.fillText(name, 869 + 203 / 2, 88 + 87 / 2);
        }

        // 现在截取中心 1000x1000 的部分
        const finalCanvas = document.createElement("canvas");
        const finalCtx = finalCanvas.getContext("2d");

        finalCanvas.width = width;
        finalCanvas.height = height;

        // 计算起始点
        const startX = (1920 - width) / 2;
        const startY = isCenter ? 1080 - height - 100 : (1080 - height) / 2;

        // 从临时 canvas 截取中心区域
        finalCtx.drawImage(
          tempCanvas,
          startX,
          startY,
          width,
          height,
          0,
          0,
          width,
          height,
        );

        // 转换最终 canvas 为 data URL (JPG 格式)
        const imageURL = finalCanvas.toDataURL("image/jpeg");

        // 创建一个用于保存的链接
        const link = document.createElement("a");
        link.href = imageURL;
        link.download = isCenter
          ? `${t("myBear.profilePictureName")}.jpg`
          : `${t("myBear.wallpaperName")}.jpg`; // 设置下载的文件名
        link.click(); // 触发下载
      })
      .catch((error) => {
        console.error("Failed to load images", error);
      });
  };

  const updateStatusHandler = async () => {
    // 先將全部裝備設為未穿戴
    mergedData.forEach((category) => {
      category.equipment.forEach((equip) => {
        equip.isWear = false;
      });
    });
    // 跑回圈看哪些裝備被穿戴，然後將bear_set更新，接著打API
    wearList.forEach(({ categoryId, equipmentId }) => {
      const categoryIndex = mergedData.findIndex(
        (category) => category.categoryId === categoryId,
      );
      const equipIndex = mergedData[categoryIndex].equipment.findIndex(
        (equip) => equip.id === equipmentId,
      );
      // 再將選中的裝備設為穿戴
      mergedData[categoryIndex].equipment[equipIndex].isWear = true;
    });
    const updateBearSetData = mergedData.map((category) => ({
      categoryId: category.categoryId,
      isLock: category.isLock,
      equipment: category.equipment.map((equip) => ({
        id: equip.id,
        isWear: equip.isWear,
        isNew: false,
      })),
    }));
    if (backgroundInfo.id) {
      updateBearSetData.push({
        categoryId: 7,
        isLock: backgroundData[0].isLock,
        equipment: backgroundData[0].equipment.map((equip) => ({
          id: equip.id,
          isWear: equip.id === backgroundInfo.id,
          isNew: false,
        })),
      });
    } else {
      updateBearSetData.push({
        categoryId: 7,
        isLock: backgroundData[0].isLock,
        equipment: backgroundData[0].equipment.map((equip) => ({
          id: equip.id,
          isWear: false,
          isNew: false,
        })),
      });
    }
    const success = await dispatch(
      updateStatus({
        user_no,
        user_lang,
        bear_type,
        user_progress,
        bear_set: updateBearSetData,
        login_at,
      }),
    );
    if (success) {
      showEquipmentHandler(false);
    } else {
      alert("Failed to update status");
    }
  };

  const changeBackgroundHandler = (id, imageInfo) => {
    if (backgroundInfo.id === id) {
      setBackgroundInfo({});
      return;
    }
    setBackgroundInfo({ id, imageInfo });
  };

  const resetBear = () => {
    showEquipmentHandler(false);
    // 將wearList清空，並將穿戴的裝備設置為api回傳的裝備
    setWearList([]);
    initBearCanvas();
  };

  const isSelected = (categoryId, equipment) => {
    const isEquipmentSelected = wearList.some(
      (item) =>
        item.categoryId === categoryId && item.equipmentId === equipment.id,
    );

    if (isEquipmentSelected) {
      return "selected";
    }

    return "";
  };

  const isBackgroundSelected = (background) => {
    if (backgroundInfo.id === background.id) {
      return "selected";
    }

    return "";
  };

  const [starmove, setStarmove] = useState(0);
  useEffect(() => {
    const windowScroll = () => {
      setStarmove(((window.scrollY * 1.285) / 1920) * -100);
    };
    console.log(starmove);
    window.addEventListener("scroll", windowScroll);

    return () => {
      window.removeEventListener("scroll", windowScroll);
    };
  }, []);

  const hasNewEqupment = mergedData.some((category) =>
    category.equipment.some((equip) => equip.isNew),
  );

  // // 监听窗口大小变化
  // useEffect(() => {
  //   // 使用 lodash 的 debounce 函数来防抖
  //   const debouncedRedraw = debounce(() => {
  //     console.log("resize");
  //     draw();
  //   }, 250); // 250毫秒内的调用将被合并为一次

  //   window.addEventListener("resize", debouncedRedraw);
  //   return () => {
  //     window.removeEventListener("resize", debouncedRedraw);
  //     debouncedRedraw.cancel(); // 清理
  //   };
  // }, []);

  // // 更新 Canvas 大小以適應 CSS 變化
  // useEffect(() => {
  //   const updateCanvasSize = () => {
  //     const canvas = canvasRef.current;
  //     const rect = canvas.getBoundingClientRect();
  //     canvas.width = rect.width;
  //     canvas.height = rect.height;
  //   };
  //   updateCanvasSize();
  //   window.addEventListener("resize", updateCanvasSize);
  //   return () => window.removeEventListener("resize", updateCanvasSize);
  // }, []);
  useEffect(() => {
    // 创建一个更新画布大小并重绘的函数
    const updateAndDraw = () => {
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      draw(); // 重新绘制画布内容
    };

    // 使用 debounce 函数来防抖
    const debouncedUpdateAndDraw = debounce(updateAndDraw, 250);

    // 注册 resize 事件监听器
    window.addEventListener("resize", debouncedUpdateAndDraw);

    // 初始执行一次以设置画布尺寸
    updateAndDraw();

    // 清理函数
    return () => {
      window.removeEventListener("resize", debouncedUpdateAndDraw);
      debouncedUpdateAndDraw.cancel(); // 取消防抖中未执行的调用
    };
  }, [draw]); // 确保当 draw 函数改变时更新依赖

  return (
    <article className={`my-bear ${showEquipment ? "open" : ""}`}>
      <canvas
        ref={hiddenCanvasRef}
        width="1920"
        height="1080"
        style={{ display: "none" }}
      ></canvas>
      <div className="star-box">
        <div className="star star01"></div>
        <div className="star star02"></div>
        <div className="star star03"></div>
        <div className="star star04"></div>
        <div className="star star05"></div>
      </div>
      <section className="bear-section">
        <div className="scroll-down">
          <div className="lottie">
            <Player
              autoplay
              loop
              src={animationData}
              style={{ height: "100%", width: "100%" }}
            ></Player>
          </div>
          <div className="scroll-text">
            <p>{t("myBear.scrollDown")}</p>
          </div>
          <figure>
            <img src={image.scrollDown} alt="" />
          </figure>
        </div>
        <div className="main">
          <div
            className="bg"
            style={{
              backgroundImage: backgroundInfo.id
                ? `url(${backgroundInfo.imageInfo.background})`
                : "",
            }}
          ></div>
          <figure className="land">
            <img src={image.bgLandSrc} alt="" />
          </figure>
          <div className="bear">
            <div className="canvas-box">
              <canvas
                ref={canvasRef}
                id="myCanvas"
                width="1920"
                height="1080"
              ></canvas>
            </div>
          </div>
          <div className="info-box">
            <div className="name">{t("myBear.hello")}</div>
            <figure className="dialog">
              <img src={image.dialogSrc} alt="" />
            </figure>
            <div className="dialog-content">
              <p
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(t("myBear.dialogContent")),
                }}
              ></p>
            </div>
            <Link to="/course">
              <div className="intro-btn">
                <p
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(t("myBear.introBtn")),
                  }}
                ></p>
                <figure>
                  <img src={image.introBtn} alt="" />
                </figure>
              </div>
            </Link>
          </div>
        </div>
        <div className="equipment">
          <div className="equipment-wrapper">
            <div className="remind">{t("myBear.equipmentRemind")}</div>
            <div
              className="title"
              onClick={
                showEquipment
                  ? () => resetBear()
                  : () => showEquipmentHandler(true)
              }
            >
              {hasNewEqupment ? <div className="new-tag"></div> : ""}
              <div className="title-wrapper">
                <div className="text">{t("myBear.equipmentTitle")}</div>
              </div>
            </div>
            <div className="equipment-box">
              {mergedData
                ? mergedData.map((category, categoryIndex) => {
                    return (
                      <div className="equipment-category" key={categoryIndex}>
                        <div className="category-name">
                          {category.title[i18n.language]}
                        </div>
                        <div className="box">
                          {category.equipment.map(
                            (equipment, equipmentIndex) => {
                              return (
                                <div
                                  className={`equipment-item ${isSelected(
                                    category.categoryId,
                                    equipment,
                                  )}`}
                                  key={equipmentIndex}
                                  onClick={() =>
                                    category.isLock
                                      ? null
                                      : wearEquipmentHandler(
                                          true,
                                          equipment.imageInfo,
                                          category.categoryId,
                                          equipment.id,
                                        )
                                  }
                                >
                                  <div className="item">
                                    <figure className="equipment-pic">
                                      {equipment.imageInfo
                                        ? equipment.imageInfo.map(
                                            (info, index) => (
                                              <img
                                                key={index}
                                                src={info.listSrc}
                                                alt=""
                                                style={{ zIndex: info.zIndex }}
                                              />
                                            ),
                                          )
                                        : ""}
                                    </figure>
                                    <div className="equipment-name">
                                      {equipment.name
                                        ? equipment.name[i18n.language]
                                        : ""}
                                      {equipment.isNew ? (
                                        <div className="new-tag equipment-new-tag"></div>
                                      ) : (
                                        ""
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            },
                          )}
                        </div>
                      </div>
                    );
                  })
                : ""}
              <div className="equipment-category scenes-category">
                <div className="category-name">{t("myBear.categoryArea")}</div>
                <div className="box">
                  {backgroundData[0]
                    ? backgroundData[0].equipment.map(
                        (background, backgroundIndex) => {
                          return (
                            <div
                              className={`equipment-item ${isBackgroundSelected(
                                background,
                              )}`}
                              key={backgroundIndex}
                              onClick={() =>
                                changeBackgroundHandler(
                                  background.id,
                                  background.imageInfo[0],
                                )
                              }
                            >
                              <div className="item">
                                <figure className="equipment-pic">
                                  {
                                    <img
                                      src={background.imageInfo[0].canvasBg}
                                      alt=""
                                    />
                                  }
                                </figure>
                                <div className="equipment-name">
                                  {background.name
                                    ? background.name[i18n.language]
                                    : ""}
                                  {background.isNew ? (
                                    <div className="new-tag equipment-new-tag"></div>
                                  ) : (
                                    ""
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        },
                      )
                    : ""}
                </div>
              </div>
            </div>
            <div className="btn-box">
              <div className="btn check" onClick={() => updateStatusHandler()}>
                {t("myBear.btnCheck")}
              </div>
              <div className="btn cancel" onClick={() => resetBear()}>
                {t("myBear.btnCancel")}
              </div>
            </div>
            <div className="qa-box">
              <Link to="/bearExplanation">
                <figure className="qa-btn">
                  <img src={image.qaBtnSrc} alt="" />
                </figure>
              </Link>
              <div className="qa-remind">
                <figure>
                  <img src={image.qaRemindLineSrc} alt="" />
                </figure>
                <p className="txt">{t("myBear.qaRemind")}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="download">
          {/* <figure className="btn">
            <img src={image.downloadBtnSrc} alt="" />
          </figure> */}
          <p
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(t("myBear.download")),
            }}
          ></p>
          <div className="box">
            <figure>
              <img src={image.downloadBoxSrc} alt="" />
            </figure>
            <div
              className="btn01"
              onClick={() => {
                handleSave(false);
              }}
            >
              {/* 根據不同語系載入不同圖片 */}
              {i18n.language === "zh" ? (
                <>
                  <figure>
                    <img src={image.download01Src} alt="" />
                  </figure>
                  <figure>
                    <img src={image.downloadHover01Src} alt="" />
                  </figure>
                </>
              ) : i18n.language === "cn" ? (
                <>
                  <figure>
                    <img src={image.downloadCn01Src} alt="" />
                  </figure>
                  <figure>
                    <img src={image.downloadHoverCn01Src} alt="" />
                  </figure>
                </>
              ) : i18n.language === "jp" ? (
                <>
                  <figure>
                    <img src={image.downloadJp01Src} alt="" />
                  </figure>
                  <figure>
                    <img src={image.downloadHoverJp01Src} alt="" />
                  </figure>
                </>
              ) : (
                <>
                  <figure>
                    <img src={image.downloadEn01Src} alt="" />
                  </figure>
                  <figure>
                    <img src={image.downloadHoverEn01Src} alt="" />
                  </figure>
                </>
              )}
              {/* <p className="txt">原始比例</p> */}
            </div>
            <div
              className="btn02"
              onClick={() => {
                handleSave(true);
              }}
            >
              {/* 根據不同語系載入不同圖片 */}
              {i18n.language === "zh" ? (
                <>
                  <figure>
                    <img src={image.download02Src} alt="" />
                  </figure>
                  <figure>
                    <img src={image.downloadHover02Src} alt="" />
                  </figure>
                </>
              ) : i18n.language === "cn" ? (
                <>
                  <figure>
                    <img src={image.downloadCn02Src} alt="" />
                  </figure>
                  <figure>
                    <img src={image.downloadHoverCn02Src} alt="" />
                  </figure>
                </>
              ) : i18n.language === "jp" ? (
                <>
                  <figure>
                    <img src={image.downloadJp02Src} alt="" />
                  </figure>
                  <figure>
                    <img src={image.downloadHoverJp02Src} alt="" />
                  </figure>
                </>
              ) : (
                <>
                  <figure>
                    <img src={image.downloadEn02Src} alt="" />
                  </figure>
                  <figure>
                    <img src={image.downloadHoverEn02Src} alt="" />
                  </figure>
                </>
              )}
              {/* <p className="txt">大頭貼</p> */}
            </div>
          </div>
          <figure className="logo">
            <img src={image.tsmcLogo} alt="" />
          </figure>
        </div>
      </section>
      <Progress></Progress>
    </article>
  );
}
